/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useState } from 'react';
import { injectIntl, defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import { Modal, ContentSwitcher, IconSwitch } from 'carbon-addons-iot-react';
import noop from 'lodash/noop';
import List from '@carbon/icons-react/lib/list/16';
import Code from '@carbon/icons-react/lib/code/16';

const messages = defineMessages({
  sourceView: 'Source View',
  formView: 'Form View',

  sourceViewErrorModalHeading: 'Invalid source',
  sourceViewErrorModalEditButton: 'Edit',
  sourceViewErrorModalRevertButton: 'Revert',
  sourceViewErrorModalMessage:
    'There are errors in the source you have entered. If you wish to leave source view you can resolve the errors by editing the source, or revert to the last valid version.',
});

const propTypes = {
  /* (inSourceView: bool) => {} */
  onChange: PropTypes.func,

  /*
   * Note: due to the way the ContentSwitcher component works, this will only determine the
   * initial state of the component. To work around this, when you need to force
   * a new value for this, reassign the component key to force it to remount
   * (and thus use this initial value).
   */
  inSourceView: PropTypes.bool,

  /**
   * When false, the "invalid source, revert or edit?" modal will be shown when the user
   * attempts to switch back to the graphical mode.
   *
   * Null indicates that a parse (or debounce timeout) is active, and the grpahical switch will be disabled
   * until this is once again set to true/false.
   *
   * Designed for usage in conjunction with values emitted by onChangeParseErrorStatus callback
   * of MonacoEditorFormik (and its derivatives).
   */
  sourceHasErrors: PropTypes.bool,
};

const defaultProps = {
  onChange: noop,
  inSourceView: false,
  sourceHasErrors: false,
};

const GRAPHICAL_VIEW_INDEX = 0;
const SOURCE_VIEW_INDEX = 1;

const SourceViewSwitcher = ({
  inSourceView,
  onChange,
  sourceHasErrors,
  intl: { formatMessage },
}) => {
  const [isSourceViewErrorsModalVisible, setSourceViewErrorsModalVisible] = useState(false);

  return (
    <>
      {isSourceViewErrorsModalVisible ? (
        <Modal
          modalHeading={formatMessage(messages.sourceViewErrorModalHeading)}
          modalAriaLabel="error"
          iconDescription={formatMessage(messages.sourceViewErrorModalEditButton)}
          onRequestClose={() => {
            // a.k.a. edit
            setSourceViewErrorsModalVisible(false);
            onChange(true);
          }}
          onRequestSubmit={() => {
            // a.k.a. edit
            setSourceViewErrorsModalVisible(false);
            onChange(true);
          }}
          onSecondarySubmit={() => {
            // a.k.a. revert to last valid
            setSourceViewErrorsModalVisible(false);
            // NOTE: no extra action required to perform the revert; invalid values entered in source view won't have been committed to Formik state
            onChange(false);
          }}
          open
          primaryButtonText={formatMessage(messages.sourceViewErrorModalEditButton)}
          secondaryButtonText={formatMessage(messages.sourceViewErrorModalRevertButton)}>
          {formatMessage(messages.sourceViewErrorModalMessage)}
        </Modal>
      ) : null}

      <ContentSwitcher
        // reassign the key to force the component to remount with the provided initial inSourceView value
        // This is a workaround for the fact that the ContentSwitcher component cannot be used in fully-controlled mode
        // Without this, when force inSourceView=false due to the "Edit" button in the SourceViewErrorsModal being clicked,
        // the "form view" icon would erroneously appear to be selected.
        key={`${isSourceViewErrorsModalVisible}-ContentSwitcher`}
        className="source-view-switcher"
        onChange={e => {
          if (e) {
            if (inSourceView && sourceHasErrors) {
              setSourceViewErrorsModalVisible(true);
            } else {
              onChange(e.index === SOURCE_VIEW_INDEX);
            }
          }
        }}
        selectedIndex={inSourceView ? SOURCE_VIEW_INDEX : GRAPHICAL_VIEW_INDEX}>
        <IconSwitch
          name="form"
          text={formatMessage(messages.formView)}
          renderIcon={List}
          index={GRAPHICAL_VIEW_INDEX}
          size="small"
          disabled={sourceHasErrors === null}
          onClick={noop}
          onKeyDown={noop}
        />
        <IconSwitch
          name="source"
          text={formatMessage(messages.sourceView)}
          renderIcon={Code}
          index={SOURCE_VIEW_INDEX}
          size="small"
          onClick={noop}
          onKeyDown={noop}
        />
      </ContentSwitcher>
    </>
  );
};

SourceViewSwitcher.propTypes = propTypes;
SourceViewSwitcher.defaultProps = defaultProps;

export default injectIntl(SourceViewSwitcher);
